import type { MakeOptional } from '@mui/x-internals/types';
import { DEFAULT_RADIUS_AXIS_KEY, DEFAULT_ROTATION_AXIS_KEY } from '../../../../constants';
import type { ScaleName } from '../../../../models';
import type {
  ChartsRotationAxisProps,
  AxisId,
  PolarAxisConfig,
  ChartsRadiusAxisProps,
} from '../../../../models/axis';
import type { DatasetType } from '../../../../models/seriesType/config';

export function defaultizeAxis<TScale extends ScaleName = ScaleName>(
  inAxis: MakeOptional<PolarAxisConfig<TScale, any>, 'id'>[] | undefined,
  dataset: Readonly<DatasetType> | undefined,
  axisName: 'rotation',
): PolarAxisConfig<TScale, any, ChartsRotationAxisProps>[];
export function defaultizeAxis<TScale extends ScaleName = ScaleName>(
  inAxis: MakeOptional<PolarAxisConfig<TScale, any>, 'id'>[] | undefined,
  dataset: Readonly<DatasetType> | undefined,
  axisName: 'radius',
): PolarAxisConfig<TScale, any, ChartsRadiusAxisProps>[];
export function defaultizeAxis<TScale extends ScaleName = ScaleName>(
  inAxis: MakeOptional<PolarAxisConfig<TScale, any>, 'id'>[] | undefined,
  dataset: Readonly<DatasetType> | undefined,
  axisName: 'rotation' | 'radius',
) {
  const DEFAULT_AXIS_KEY: AxisId =
    axisName === 'rotation' ? DEFAULT_ROTATION_AXIS_KEY : DEFAULT_RADIUS_AXIS_KEY;

  const inputAxes: MakeOptional<PolarAxisConfig<ScaleName, any>, 'id'>[] =
    inAxis && inAxis.length > 0 ? inAxis : [{ id: DEFAULT_AXIS_KEY }];

  return inputAxes.map((axisConfig, index) => {
    const id = `defaultized-${axisName}-axis-${index}`;
    const dataKey = axisConfig.dataKey;

    if (axisConfig.data !== undefined || (dataKey === undefined && !axisConfig.valueGetter)) {
      return {
        id,
        ...axisConfig,
      } as PolarAxisConfig<TScale, any>;
    }
    if (dataset === undefined) {
      throw new Error(
        `MUI X Charts: The ${axisName}-axis uses \`dataKey\` or \`valueGetter\` but no \`dataset\` is provided. ` +
          'When using dataKey or valueGetter, a dataset must be provided to retrieve the axis data. ' +
          `Either provide a dataset prop or use the data property directly on the ${axisName}-axis.`,
      );
    }

    return {
      id,
      data: axisConfig.valueGetter
        ? dataset.map((d) => axisConfig.valueGetter!(d))
        : dataset.map((d) => d[dataKey!]),
      ...axisConfig,
    } as PolarAxisConfig<TScale, any>;
  });
}
